import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchSalonDashboard } from '../../api/analytics';
import StatCard from '../../components/admin/StatCard';
import StatusBadge from '../../components/appointments/StatusBadge';
import PendingApprovalsPanel from '../../components/dashboard/PendingApprovalsPanel';
import DashboardShell from '../../components/ui/DashboardShell';
import { CardSkeleton, ListSkeleton } from '../../components/ui/Skeleton';
import { SALON_DASHBOARD_POLL_MS } from '../../constants/polling';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { getChartTheme } from '../../utils/chartTheme';

const TEAL = '#14b8a6';

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const SalonManagerDashboard = () => {
  const { t } = useTranslation();
  const { user, tenantId } = useAuth();
  const { theme } = useTheme();
  const chartTheme = getChartTheme(theme);
  const { showToast } = useToast();
  const hour = new Date().getHours();
  const greeting =
    hour < 12
      ? t('salon.greeting.morning')
      : hour < 17
        ? t('salon.greeting.afternoon')
        : t('salon.greeting.evening');
  const seenPendingRef = useRef(null);

  useEffect(() => {
    seenPendingRef.current = null;
  }, [tenantId]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['salon-dashboard', tenantId],
    queryFn: fetchSalonDashboard,
    enabled: !!tenantId,
    staleTime: 0,
    refetchInterval: SALON_DASHBOARD_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const chartSeries = data?.incomeSeries || [];
  const pendingAppts = useMemo(() => data?.pendingAppointmentRequests || [], [data]);
  const pendingOrds = useMemo(() => data?.pendingOrderRequests || [], [data]);
  const pendingReviews = data?.pendingReviews || 0;

  useEffect(() => {
    if (!data) return;

    const orderIds = pendingOrds.map((o) => o._id);
    const apptIds = pendingAppts.map((a) => a._id);
    const snapshot = JSON.stringify({ orderIds, apptIds, pendingReviews });

    if (seenPendingRef.current === null) {
      seenPendingRef.current = snapshot;
      return;
    }

    if (seenPendingRef.current === snapshot) return;

    const prev = JSON.parse(seenPendingRef.current);
    const newOrders = orderIds.filter((id) => !prev.orderIds.includes(id));
    const newAppts = apptIds.filter((id) => !prev.apptIds.includes(id));
    const reviewsIncreased = pendingReviews > (prev.pendingReviews || 0);

    newOrders.forEach(() => {
      showToast(t('toast.newOrderPending'), 'info');
    });
    newAppts.forEach(() => {
      showToast(t('toast.newBookingPending'), 'info');
    });
    if (reviewsIncreased) {
      showToast(t('toast.newReviewPending'), 'info');
    }

    seenPendingRef.current = snapshot;
  }, [data, pendingOrds, pendingAppts, pendingReviews, showToast, t]);

  if (!tenantId) {
    const isAdmin = user?.role === 'SUPER_ADMIN';
    return (
      <DashboardShell title={t('salon.dashboard.title')} subtitle={t('salon.dashboard.overviewSubtitle')}>
        <div className="max-w-lg space-y-3 rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900/60">
          <p className="text-zinc-700 dark:text-zinc-300">
            {isAdmin ? t('salon.dashboard.adminNeedsImpersonation') : t('salon.dashboard.noTenant')}
          </p>
          {isAdmin && (
            <Link
              to="/admin/tenants"
              className="inline-flex rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white hover:bg-glow-500"
            >
              {t('salon.dashboard.openTenants')}
            </Link>
          )}
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell
      title={`${greeting}, ${user?.firstName}`}
      subtitle={t('salon.dashboard.subtitle')}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <a
          href="/salon/pos"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-glow-600/40 bg-glow-600/10 px-4 py-2 text-sm font-medium text-glow-300 transition hover:bg-glow-600/20"
        >
          <span>🧾</span>
          {t('pos.title')}
        </a>
      </div>
      <div className="mb-4 flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-500">
        <span
          className={`inline-block h-2 w-2 rounded-full ${isFetching ? 'animate-pulse bg-glow-400' : 'bg-emerald-500'}`}
          aria-hidden
        />
        <span>
          {isFetching ? t('salon.dashboard.updating') : t('salon.dashboard.live')}
          {' · '}
          {t('salon.dashboard.refreshEvery', { seconds: SALON_DASHBOARD_POLL_MS / 1000 })}
        </span>
      </div>
      {isLoading && (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
          <ListSkeleton rows={4} />
        </>
      )}

      {!isLoading && data && (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label={t('salon.dashboard.revenueToday')}
              value={formatLkr(data.revenueToday)}
              icon="💰"
              trend={t('salon.dashboard.trendVsYesterday', {
                change: `${data.revenueTodayChange >= 0 ? '+' : ''}${data.revenueTodayChange}`,
              })}
              trendUp={data.revenueTodayChange >= 0}
            />
            <StatCard
              label={t('salon.dashboard.revenueThisMonth')}
              value={formatLkr(data.revenueMonth)}
              icon="📈"
              trend={t('salon.dashboard.trendVsLastMonth', {
                change: `${data.revenueMonthChange >= 0 ? '+' : ''}${data.revenueMonthChange}`,
              })}
              trendUp={data.revenueMonthChange >= 0}
            />
            <StatCard
              label={t('salon.dashboard.todayAppointments')}
              value={data.todayAppointmentsCount}
              icon="📅"
              trend={t('salon.dashboard.thisMonthCount', { count: data.monthAppointments })}
            />
            <StatCard
              label={t('salon.dashboard.pendingActions')}
              value={
                (data.pendingAppointments || 0) +
                (data.pendingOrders || 0) +
                (data.pendingAssignments || 0) +
                (data.pendingReviews || 0)
              }
              icon="⏳"
              trend={t('salon.dashboard.needsReview')}
            />
          </div>

          <PendingApprovalsPanel appointments={pendingAppts} orders={pendingOrds} />

          {pendingReviews > 0 && (
            <div className="mb-8 rounded-xl border border-amber-800/40 bg-amber-950/20 p-4">
              <p className="text-sm text-amber-200">
                {t('salon.dashboard.pendingReviews', { count: pendingReviews })}{' '}
                <Link to="/salon/reviews" className="text-glow-400 hover:underline">
                  {t('salon.dashboard.reviewInReviews')}
                </Link>
              </p>
            </div>
          )}

          {data.pendingAssignments > 0 && (
            <div className="mb-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {t('salon.dashboard.pendingAssignments', { count: data.pendingAssignments })}{' '}
                —{' '}
                <Link to="/salon/appointments" className="text-glow-400 hover:underline">
                  {t('salon.dashboard.reviewInAppointments')}
                </Link>
              </p>
            </div>
          )}

          <div className="mb-8 grid gap-6 lg:grid-cols-5">
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 lg:col-span-3">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-medium">{t('salon.dashboard.revenueThisYear')}</h3>
                <Link to="/salon/analytics" className="text-xs text-glow-400 hover:underline">
                  {t('salon.dashboard.fullAnalytics')}
                </Link>
              </div>
              {chartSeries.length === 0 ? (
                <p className="py-16 text-center text-sm text-zinc-600 dark:text-zinc-500">{t('salon.dashboard.noRevenueData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="dashRevenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
                    <XAxis dataKey="label" tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                    <YAxis tick={{ fill: chartTheme.tick, fontSize: 11 }} tickFormatter={(v) => v.toLocaleString()} />
                    <Tooltip
                      contentStyle={{
                        background: chartTheme.tooltipBg,
                        border: `1px solid ${chartTheme.tooltipBorder}`,
                        color: chartTheme.tooltipColor,
                      }}
                      formatter={(value) => [formatLkr(value), t('salon.dashboard.revenueLabel')]}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={TEAL}
                      fill="url(#dashRevenueFill)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-600 dark:text-zinc-500">
                <span>{t('salon.dashboard.servicesMonth')}: {formatLkr(data.serviceRevenueMonth)}</span>
                <span>{t('salon.dashboard.productsMonth')}: {formatLkr(data.productRevenueMonth)}</span>
                <span>{t('salon.dashboard.ordersMonth')}: {data.monthOrders}</span>
              </div>
            </div>

            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-medium">{t('salon.dashboard.todaysSchedule')}</h3>
                <Link to="/salon/appointments" className="text-xs text-glow-400 hover:underline">
                  {t('salon.dashboard.allAppointments')}
                </Link>
              </div>
              {data.todayAppointments.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-600 dark:text-zinc-500">{t('salon.dashboard.noAppointmentsToday')}</p>
              ) : (
                <ul className="space-y-3">
                  {data.todayAppointments.map((a) => (
                    <li
                      key={a._id}
                      className="flex items-start justify-between gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-sm">{a.serviceName}</p>
                        <p className="truncate text-xs text-zinc-600 dark:text-zinc-500">
                          {a.clientName} · {a.branchName}
                        </p>
                        <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-500">{formatLkr(a.price)}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300">{a.time}</p>
                        <StatusBadge status={a.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/salon/appointments"
              className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              {t('salon.dashboard.manageAppointments')}
            </Link>
            <Link
              to="/salon/orders"
              className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              {t('salon.dashboard.manageOrders')}
            </Link>
            <Link
              to="/salon/analytics"
              className="rounded-lg bg-glow-600 px-4 py-2 text-sm text-white hover:bg-glow-500"
            >
              {t('salon.dashboard.viewAnalytics')}
            </Link>
          </div>
        </>
      )}
    </DashboardShell>
  );
};

export default SalonManagerDashboard;
